import { useCallback } from 'react';
import { useTranslation } from '@/i18n';
import { useExport } from '@/hooks/ui/useExport';
import { useUI } from '@/hooks/ui/useUI';
import { appMobileProfileEmployeesService } from '@/api/domains/employee/appMobileProfileEmployees';
import type { UIExportFormat } from '@/types/fileExport.types';


// ── Return type interface ──────────────────────────────────────────────────

export interface UseMobileAppPermissionsExportResult {
  /** Descarga el Excel de asignaciones del perfil seleccionado. Solo formato EXCEL. */
  exportAssignments: (format: UIExportFormat) => Promise<void>;
  isExporting: boolean;
}

/**
 * Descarga a Excel el listado de asignaciones del perfil APP móvil seleccionado.
 * Requiere un perfil seleccionado; sin él no se dispara la exportación.
 */
export function useMobileAppPermissionsExport(
  selectedProfileId: number | null,
): UseMobileAppPermissionsExportResult {
  const { t: translate } = useTranslation();
  const { showError } = useUI();

  const { onExport: exportFile, isExporting } = useExport({
    exportFn: () =>
      appMobileProfileEmployeesService.export({ appProfileId: selectedProfileId as number }),
    successMessage: translate('authorization:mobileAppPermissions.messages.exportSuccess'),
    errorMessage: translate('authorization:mobileAppPermissions.messages.exportError'),
  });

  const exportAssignments = useCallback(
    async (format: UIExportFormat) => {
      if (selectedProfileId === null) return;
      if (format !== 'EXCEL') {
        showError(translate('authorization:mobileAppPermissions.messages.exportError'));
        return;
      }
      await exportFile();
    },
    [selectedProfileId, exportFile, showError, translate],
  );

  return { exportAssignments, isExporting };
}

export default useMobileAppPermissionsExport;
